/**
 * RefereesPage.js - Dedicated page for managing referees (user-scoped, cross-league)
 */

import React, { useState, useEffect, useCallback } from 'react';
import * as api from '../services/api';
import { getSportConfig } from '../utils/sportConfig';
import './RefereesPage.css';

const ALL_SPORTS = ['general', 'soccer', 'basketball', 'volleyball', 'softball', 'hockey', 'football'];

function RefereesPage({ onBack }) {
  const [referees, setReferees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [toast, setToast] = useState(null);

  // Add form state
  const [showAddForm, setShowAddForm] = useState(false);
  const [addForm, setAddForm] = useState({ name: '', email: '', phone: '', sports: [], notes: '' });
  const [addLoading, setAddLoading] = useState(false);

  // Edit state
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', email: '', phone: '', sports: [], notes: '' });
  const [editLoading, setEditLoading] = useState(false);

  const showToast = useCallback((message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  }, []);

  const fetchReferees = useCallback(async () => {
    try {
      const result = await api.getReferees();
      setReferees(result.referees);
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    fetchReferees();
  }, [fetchReferees]);

  const toggleSport = (sports, sport) => {
    if (sports.includes(sport)) {
      return sports.filter(s => s !== sport);
    }
    return [...sports, sport];
  };

  const handleCreate = async () => {
    if (!addForm.name.trim()) return;
    setAddLoading(true);
    try {
      const result = await api.createReferee({
        name: addForm.name.trim(),
        email: addForm.email.trim() || null,
        phone: addForm.phone.trim() || null,
        sports: addForm.sports.length > 0 ? addForm.sports : null,
        notes: addForm.notes.trim() || null
      });
      setReferees(prev => [...prev, { ...result.referee, gameCount: 0, leagues: [] }].sort((a, b) => a.name.localeCompare(b.name)));
      setAddForm({ name: '', email: '', phone: '', sports: [], notes: '' });
      setShowAddForm(false);
      showToast('Referee created');
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setAddLoading(false);
    }
  };

  const handleEdit = (referee) => {
    setEditingId(referee.id);
    setEditForm({
      name: referee.name,
      email: referee.email || '',
      phone: referee.phone || '',
      sports: referee.sports || [],
      notes: referee.notes || ''
    });
  };

  const handleSaveEdit = async () => {
    if (!editForm.name.trim()) return;
    setEditLoading(true);
    try {
      const result = await api.updateReferee(editingId, {
        name: editForm.name.trim(),
        email: editForm.email.trim() || null,
        phone: editForm.phone.trim() || null,
        sports: editForm.sports.length > 0 ? editForm.sports : null,
        notes: editForm.notes.trim() || null
      });
      setReferees(prev =>
        prev.map(r => r.id === editingId ? { ...r, ...result.referee } : r)
          .sort((a, b) => a.name.localeCompare(b.name))
      );
      setEditingId(null);
      showToast('Referee updated');
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setEditLoading(false);
    }
  };

  const handleDelete = async (referee) => {
    if (!window.confirm(`Delete referee "${referee.name}"?`)) return;
    try {
      await api.deleteReferee(referee.id);
      setReferees(prev => prev.filter(r => r.id !== referee.id));
      showToast('Referee deleted');
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  const filtered = referees.filter(r => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      r.name.toLowerCase().includes(q) ||
      (r.email && r.email.toLowerCase().includes(q)) ||
      (r.phone && r.phone.toLowerCase().includes(q))
    );
  });

  const renderSportCheckboxes = (sports, onChange) => (
    <div className="referees-sports-section">
      <div className="referees-sports-label">Sports (leave empty for all sports)</div>
      <div className="referees-sport-checkbox-grid">
        {ALL_SPORTS.map(sport => (
          <label
            key={sport}
            className={sports.includes(sport) ? 'referees-sport-active' : ''}
          >
            <input
              type="checkbox"
              checked={sports.includes(sport)}
              onChange={() => onChange(sport)}
            />
            {getSportConfig(sport).label}
          </label>
        ))}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="referees-container">
        <p style={{ textAlign: 'center', color: 'var(--color-text-secondary)' }}>Loading referees...</p>
      </div>
    );
  }

  return (
    <div className="referees-container">
      <button className="referees-back" onClick={onBack}>&larr; Dashboard</button>

      <div className="referees-header">
        <h2>
          Referees
          <span className="referees-count">{referees.length}</span>
        </h2>
        <button
          className="referees-btn-primary"
          onClick={() => setShowAddForm(!showAddForm)}
        >
          {showAddForm ? 'Cancel' : '+ Add Referee'}
        </button>
      </div>

      {showAddForm && (
        <div className="referees-add-form">
          <h3>New Referee</h3>
          <div className="referees-form-grid">
            <input
              placeholder="Name *"
              value={addForm.name}
              onChange={e => setAddForm(prev => ({ ...prev, name: e.target.value }))}
            />
            <input
              placeholder="Email"
              value={addForm.email}
              onChange={e => setAddForm(prev => ({ ...prev, email: e.target.value }))}
            />
            <input
              placeholder="Phone"
              value={addForm.phone}
              onChange={e => setAddForm(prev => ({ ...prev, phone: e.target.value }))}
            />
            <textarea
              placeholder="Notes"
              value={addForm.notes}
              onChange={e => setAddForm(prev => ({ ...prev, notes: e.target.value }))}
            />
            {renderSportCheckboxes(addForm.sports, (sport) =>
              setAddForm(prev => ({ ...prev, sports: toggleSport(prev.sports, sport) }))
            )}
          </div>
          <div className="referees-form-actions">
            <button
              className="referees-btn-primary"
              onClick={handleCreate}
              disabled={!addForm.name.trim() || addLoading}
            >
              {addLoading ? 'Creating...' : 'Create Referee'}
            </button>
          </div>
        </div>
      )}

      {referees.length > 3 && (
        <div className="referees-search">
          <input
            placeholder="Search by name, email, or phone..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button onClick={() => setSearchQuery('')}>Clear</button>
          )}
        </div>
      )}

      {filtered.length === 0 ? (
        <div className="referees-empty">
          {referees.length === 0 ? (
            <>
              <p><strong>No referees yet</strong></p>
              <p>Add referees here and assign them to games from the Dashboard.</p>
            </>
          ) : (
            <p>No referees matching "{searchQuery}"</p>
          )}
        </div>
      ) : (
        <div className="referees-list">
          {filtered.map(referee => (
            <div key={referee.id} className="referees-card">
              {editingId === referee.id ? (
                /* Edit mode */
                <div>
                  <div className="referees-form-grid">
                    <input
                      placeholder="Name *"
                      value={editForm.name}
                      onChange={e => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                    />
                    <input
                      placeholder="Email"
                      value={editForm.email}
                      onChange={e => setEditForm(prev => ({ ...prev, email: e.target.value }))}
                    />
                    <input
                      placeholder="Phone"
                      value={editForm.phone}
                      onChange={e => setEditForm(prev => ({ ...prev, phone: e.target.value }))}
                    />
                    <textarea
                      placeholder="Notes"
                      value={editForm.notes}
                      onChange={e => setEditForm(prev => ({ ...prev, notes: e.target.value }))}
                    />
                    {renderSportCheckboxes(editForm.sports, (sport) =>
                      setEditForm(prev => ({ ...prev, sports: toggleSport(prev.sports, sport) }))
                    )}
                  </div>
                  <div className="referees-form-actions" style={{ marginTop: '0.5rem' }}>
                    <button
                      className="referees-btn-primary"
                      onClick={handleSaveEdit}
                      disabled={!editForm.name.trim() || editLoading}
                    >
                      {editLoading ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      className="referees-btn-secondary"
                      onClick={() => setEditingId(null)}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                /* Display mode */
                <div className="referees-card-header">
                  <div className="referees-card-info">
                    <p className="referees-card-name">
                      {referee.name}
                      {referee.sports && referee.sports.length > 0 && (
                        <span className="referees-sport-badges">
                          {referee.sports.map(sport => (
                            <span key={sport} className="referees-sport-badge">
                              {getSportConfig(sport).label}
                            </span>
                          ))}
                        </span>
                      )}
                    </p>
                    {referee.email && (
                      <p className="referees-card-detail">{referee.email}</p>
                    )}
                    {referee.phone && (
                      <p className="referees-card-detail">{referee.phone}</p>
                    )}
                    {referee.notes && (
                      <p className="referees-card-notes">{referee.notes}</p>
                    )}
                    {referee.leagues && referee.leagues.length > 0 && (
                      <div className="referees-card-leagues">
                        <div className="referees-card-leagues-label">Assigned in</div>
                        {referee.leagues.map(l => (
                          <span key={l.id} className="referees-league-tag">
                            {l.name}
                          </span>
                        ))}
                        <span className="referees-game-count">{referee.gameCount} game{referee.gameCount !== 1 ? 's' : ''}</span>
                      </div>
                    )}
                  </div>
                  <div className="referees-card-actions">
                    <button onClick={() => handleEdit(referee)}>Edit</button>
                    <button className="referees-btn-delete" onClick={() => handleDelete(referee)}>Delete</button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {toast && (
        <div className={`referees-toast ${toast.type}`}>{toast.message}</div>
      )}
    </div>
  );
}

export default RefereesPage;
